<template>
  <div id="app">
    <template v-if="isLoginPage">
      <router-view></router-view>
    </template>
    
    <template v-else>
      <el-container class="layout-container" :direction="isMobile ? 'vertical' : 'horizontal'">
        <!-- 移动端顶部导航栏 -->
        <el-header v-if="isMobile" class="mobile-header" height="50px">
          <div class="header-left" @click="drawerVisible = true">
            <i class="el-icon-menu"></i>
          </div>
          <div class="header-title">{{ currentTitle }}</div>
          <div class="header-right">
            <el-dropdown trigger="click" @command="handleUserCommand">
              <i class="el-icon-user"></i>
              <el-dropdown-menu slot="dropdown">
                <el-dropdown-item disabled>{{ displayName }}</el-dropdown-item>
                <el-dropdown-item icon="el-icon-switch-button" command="logout">退出登录</el-dropdown-item>
              </el-dropdown-menu>
            </el-dropdown>
          </div>
        </el-header>
        
        <!-- 移动端抽屉菜单 -->
        <el-drawer
          v-if="isMobile"
          :visible.sync="drawerVisible"
          direction="ltr"
          :with-header="false"
          size="220px"
          class="mobile-drawer"
        >
          <div class="drawer-header">
            <span class="drawer-title">C学</span>
            <i class="el-icon-close" @click="drawerVisible = false"></i>
          </div>
          <el-menu
            :default-active="activeMenu"
            class="mobile-menu"
            router
            @select="handleMenuSelect"
          >
            <el-menu-item 
              v-for="item in menuItems" 
              :key="item.path" 
              :index="item.path"
              :route="item.path"
            >
              <i :class="item.icon"></i>
              <span>{{ item.title }}</span>
            </el-menu-item>
          </el-menu>
        </el-drawer>

        <!-- PC端左侧菜单 -->
        <el-aside v-if="!isMobile" :width="asideWidth" class="sidebar" :class="{ 'sidebar-collapsed': isCollapsed }">
          <el-menu
            :default-active="activeMenu"
            class="el-menu-vertical"
            :collapse-transition="false"
            router
          >
            <el-menu-item 
              v-for="item in menuItems" 
              :key="item.path" 
              :index="item.path"
              :route="item.path"
            >
              <i :class="item.icon"></i>
              <template #title>
                <span v-show="!isCollapsed">{{ item.title }}</span>
              </template>
            </el-menu-item>
          </el-menu>
          <div class="collapse-btn" @click="toggleCollapse">
            <i :class="isCollapsed ? 'el-icon-d-arrow-right' : 'el-icon-d-arrow-left'"></i>
          </div>
        </el-aside>

        <!-- 右侧内容 -->
        <el-main class="main-content" :class="{ 'mobile-main': isMobile }">
          <keep-alive :include="cachedViews">
            <router-view :key="$route.name" />
          </keep-alive>
        </el-main>
      </el-container>
    </template>
  </div>
</template>

<script>
import { BREAKPOINTS } from './utils/device'

export default {
  name: 'App',
  data() {
    return {
      isCollapsed: false,
      isMobile: false,
      drawerVisible: false,
      allMenuItems: [
        {
          path: '/gap-up-analysis',
          icon: 'el-icon-data-analysis',
          title: '补涨分析'
        },
        {
          path: '/bidding-analysis',
          icon: 'el-icon-s-finance',
          title: '竞价分析'
        },
        {
          path: '/first-board-preselection',
          icon: 'el-icon-s-opportunity',
          title: '首板预选'
        },
        {
          path: '/concept-match',
          icon: 'el-icon-connection',
          title: '概念匹配'
        },
        {
          path: '/trend-analysis',
          icon: 'el-icon-data-line',
          title: '趋势分析'
        },
        {
          path: '/review-notes',
          icon: 'el-icon-edit-outline',
          title: '复盘笔记'
        },
        {
          path: '/data-backtest',
          icon: 'el-icon-s-data',
          title: '数据回测'
        },
        {
          path: '/history-first-board',
          icon: 'el-icon-document',
          title: '历史首板'
        },
        {
          path: '/auction-top',
          icon: 'el-icon-s-flag',
          title: '竞价TOP'
        },
        {
          path: '/leader-command-center',
          icon: 'el-icon-s-marketing',
          title: '龙头指挥中心'
        },
        {
          path: '/emotion-node-analysis',
          icon: 'el-icon-data-board',
          title: '情绪节点分析'
        },
        {
          path: '/cycle-evolution-trial',
          icon: 'el-icon-data-analysis',
          title: '周期演化图'
        },
        {
          path: '/cycle-chart-test',
          icon: 'el-icon-data-line',
          title: '周期图测试页'
        }
      ]
    };
  },
  computed: {
    menuItems() {
      return this.allMenuItems;
    },
    asideWidth() {
      return this.isCollapsed ? '64px' : '200px';
    },
    isLoginPage() {
      return this.$route.path === '/login' || this.$route.path === '/system-crashed';
    },
    activeMenu() {
      const currentPath = this.$route.path;
      const matchedItem = this.menuItems.find(item => {
        if (currentPath === item.path) return true;
        if (currentPath.startsWith(item.path + '/')) return true;
        return false;
      });
      return matchedItem ? matchedItem.path : this.menuItems[0].path;
    },
    currentTitle() {
      const currentPath = this.$route.path;
      const matchedItem = this.menuItems.find(item => currentPath === item.path);
      return matchedItem ? matchedItem.title : 'C学';
    },
    cachedViews() {
      return this.$router.options.routes
        .filter((route) => route.meta && route.meta.keepAlive && route.component && route.component.name)
        .map((route) => route.component.name);
    },
    displayName() {
      try {
        const info = JSON.parse(localStorage.getItem('userInfo') || '{}');
        return String(info.displayName || info.remark || info.username || '').trim() || '用户';
      } catch (_err) {
        return '用户';
      }
    }
  },
  methods: {
    toggleCollapse() {
      this.isCollapsed = !this.isCollapsed;
    },
    checkScreenSize() {
      const width = window.innerWidth;
      this.isMobile = width <= BREAKPOINTS.MOBILE;
      
      if (width <= BREAKPOINTS.TABLET && !this.isMobile && !this.isCollapsed) {
        this.isCollapsed = true;
      }
    },
    handleMenuSelect() {
      this.drawerVisible = false;
    },
    handleUserCommand(command) {
      if (command === 'logout') {
        this.handleLogout();
      }
    },
    handleLogout() {
      this.$confirm('确定要退出登录吗?', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        localStorage.removeItem('token');
        localStorage.removeItem('userInfo');
        this.$message.success('已退出登录');
        this.$router.push('/login');
      }).catch(() => {});
    }
  },
  mounted() {
    this.checkScreenSize();
    window.addEventListener('resize', this.checkScreenSize);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.checkScreenSize);
  }
}
</script>

<style lang="scss" scoped>
#app {
  height: 100vh;
  margin: 0;
  padding: 0;
  
  .layout-container {
    height: 100%;
  }
  
  .mobile-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: #fff;
    padding: 0 15px;
    
    .header-left, .header-right {
      width: 40px;
      height: 40px;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      
      i {
        font-size: 20px;
        color: #fff;
      }
    }
    
    .header-title {
      font-size: 16px;
      font-weight: 500;
    }
    
    .el-dropdown i {
      color: #fff;
      cursor: pointer;
    }
  }
  
  .sidebar {
    position: relative;
    transition: width 0.3s;
    z-index: 1000;
    box-shadow: 2px 0 6px rgba(0, 21, 41, 0.35);
    
    .el-menu {
      border-right: none;
      height: calc(100% - 48px);
    }
    
    .el-menu-vertical {
      border-right: none;
      
      :deep(.el-menu-item) {
        color: #000;
        
        &:hover {
          background-color: #263445 !important;
          color: #fff;
        }
        
        &.is-active {
          background-color: #1890ff !important;
          color: #fff;
        }
        
        i {
          color: inherit;
          margin-right: 16px;
          font-size: 18px;
        }
      }
    }

    .collapse-btn {
      position: absolute;
      bottom: 0;
      left: 0;
      width: 100%;
      text-align: center;
      cursor: pointer;
      padding: 12px 0;
      transition: background-color 0.3s;
      background-color: #263445;
      color: #fff;
      border-top: 1px solid rgba(255, 255, 255, 0.1);
      
      &:hover {
        background-color: #1d2b3a;
      }
      
      i {
        font-size: 16px;
      }
    }
  }
  
  .main-content {
    padding: 20px;
    background-color: #f8f7fc;
    transition: margin-left 0.3s;
    
    &.mobile-main {
      padding: 10px;
    }
  }
  
  @media screen and (max-width: 1024px) {
    .collapse-btn {
      display: none;
    }
    
    .sidebar {
      width: 64px !important;
    }
    
    .main-content {
      padding: 10px !important;
    }
  }
}

.mobile-drawer {
  :deep(.el-drawer__body) {
    padding: 0;
    height: 100%;
  }
  
  .drawer-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 15px 20px;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: #fff;
    
    .drawer-title {
      font-size: 18px;
      font-weight: 600;
    }
    
    .el-icon-close {
      font-size: 18px;
      cursor: pointer;
    }
  }
  
  .mobile-menu {
    border-right: none;
    height: calc(100% - 54px);
    
    :deep(.el-menu-item) {
      height: 50px;
      line-height: 50px;
      
      i {
        margin-right: 12px;
        font-size: 18px;
      }
      
      &:hover {
        background-color: #f5f7fa;
      }
      
      &.is-active {
        background-color: #ecf5ff;
        color: #409eff;
      }
    }
  }
}
</style>

<style lang="scss">
html, body {
  margin: 0;
  padding: 0;
  height: 100%;
  overflow: hidden;
}

#app {
  .el-menu--collapse .el-menu-item {
    span {
      display: none;
    }
    i {
      margin-right: 0;
    }
  }
  
  .el-main {
    height: 100%;
    overflow: auto;
  }

  .el-input__inner,
  .el-select .el-select__input {
    color: #606266;
    -webkit-text-fill-color: #606266;
  }
}

@media screen and (max-width: 768px) {
  .el-table {
    font-size: 13px;
    
    .el-table__header-wrapper .cell,
    .el-table__body-wrapper .cell {
      white-space: nowrap;
    }
  }
  
  .el-dialog {
    width: 90% !important;
    margin-top: 10vh !important;
    
    .el-dialog__body {
      padding: 15px;
    }
  }

  .el-message-box {
    width: 85% !important;
    max-width: 400px;
  }
  
  .el-form-item {
    margin-bottom: 16px;
    
    .el-form-item__label {
      padding-bottom: 5px;
    }
  }
  
  .el-date-editor {
    width: 100% !important;
  }
  
  .el-select {
    width: 100%;
  }

  .el-badge__content.is-fixed {
    top: 4px !important;
    right: 16px !important;
  }
}
</style>
